import { useEffect, useRef } from "react";
import { findShellApp, loadLastView, saveLastView } from "../../lib/apps";

// =====================================================================
// SatelliteView — rendert eine Satellite-App als Iframe im os-main-Slot.
// =====================================================================
// Ablauf:
//  1. Beim Mount initial-src setzen: gespeicherter lastView falls vorhanden
//     und mit `basePath` beginnt UND ein gueltiger Satellite-Session-Cookie
//     existiert, sonst `defaultEmbedSrc` (/launch/<app>).
//  2. Bei jedem Iframe-`load` pruefen ob die Satellite-App eine Auth-Fehler-
//     Seite zeigt (Session-Cookie fehlt). Falls ja, automatisch den
//     SSO-Launch-Flow erneut triggern.
//  3. Bei jedem Iframe-`load` lesen wir `contentWindow.location.pathname`
//     (same-origin) und persistieren ihn in localStorage unter
//     `lastView:<appId>`.
//  4. Beim Unmount Snapshot fixieren (falls Wechsel ohne reload).
//
// Die Iframe-URL bleibt nach Anlegen ein controlled-once-Wert; React darf
// `src` nicht neu schreiben, sobald der User innerhalb der App navigiert
// — sonst wuerde jeder Render die App neu laden.
//
// FIX 2026-05-17: Nach Railway-Redeploy gehen In-Memory-Sessions der
// Satellites verloren. Ohne Cookie-Check lud der Iframe den lastView
// direkt, die App zeigte "Bitte melde dich ueber das Dashboard an" und
// der User musste manuell neu starten. Jetzt:
// (a) Vor lastView-Nutzung pruefen ob der Session-Cookie existiert.
// (b) onLoad: wenn die App keinen Session-Cookie hat → auto-relaunch.
//
// FIX 2026-07-08 v3: InvalidStateError beim App-Wechsel beseitigt.
// Das `about:blank`-Zwischenschritt war die Ursache: Firefox wirft einen
// browser-internen InvalidStateError wenn ein iframe zu about:blank
// navigiert und dabei aus dem DOM entfernt wird. Loesung: about:blank
// komplett eliminiert. Stattdessen direkte Navigation zur Ziel-URL.
// Fuer den Reload-Fall (gleiche URL erzwingen) wird
// contentWindow.location.replace() verwendet.
//
// FIX 2026-08-11: Wenn der Session-Cookie existiert aber ungueltig ist
// (z.B. nach Railway-Redeploy), redirectet die App zu /login. Das Iframe
// zeigt dann die Hub-Login-Seite (die wiederum zur Default-App navigiert).
// Fix: Erkennung ob das Iframe auf /login oder / gelandet ist und dann
// automatisch den SSO-Relaunch triggern.
// =====================================================================

/** Mapping: appId → Cookie-Name des Satellite-Session-Cookies */
export const SESSION_COOKIE_MAP: Record<string, string> = {
  notes: "notes_session",
  liquid: "liqui_session",
  money: "money_session",
  vertrag: "vertrag_session",
  fair: "fair_kapa_session",
  briefing: "briefing_session",
  life: "life_session",
  scan: "scan_session",

  regelwerke: "regelwerke_session",
  time: "time_session",
  "back-office": "backoffice_session",
  "coorconn-liquid": "coorconn_liqui_session",
  story: "story_session",
};

/** Prueft ob ein bestimmter Cookie im Browser existiert */
function hasCookie(name: string): boolean {
  return document.cookie.split(";").some(c => c.trim().startsWith(name + "="));
}

/**
 * Navigate an iframe to a URL without using about:blank as intermediate.
 * Uses contentWindow.location.replace() to force reload even if the URL
 * is the same as the current one (avoids browser caching).
 * Falls back to setting iframe.src if contentWindow is not accessible.
 */
function navigateIframe(iframe: HTMLIFrameElement | null, url: string): void {
  if (!iframe) return;
  try {
    // Try contentWindow.location.replace first — this avoids the about:blank
    // intermediate step that causes InvalidStateError in Firefox.
    const win = iframe.contentWindow;
    if (win) {
      win.location.replace(url);
      return;
    }
  } catch {
    // cross-origin or detached — fall through to src assignment
  }
  try {
    iframe.src = url;
  } catch {
    // InvalidStateError: iframe was detached — safe to ignore.
  }
}

type Props = { appId: string };

export function SatelliteView({ appId }: Props) {
  const app = findShellApp(appId);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const relaunchAttemptedRef = useRef(false);
  /** Tracks whether this component instance is still mounted */
  const mountedRef = useRef(true);

  // Story muss auf Smartphones als eigene PWA laufen. Ein Hub-Hash wie
  // `combsol.app/#/story` wird von Android nur als Hub-Webverknuepfung
  // installiert und kann deshalb nicht das Story-Manifest bzw. dessen Icon
  // verwenden. Der Top-Level-Launch beendet den Hub-Kontext, setzt die
  // Satellite-Session und landet anschliessend auf `/story/`.
  useEffect(() => {
    if (appId !== "story" || !app?.defaultEmbedSrc) return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    window.location.replace(app.defaultEmbedSrc);
  }, [appId, app?.defaultEmbedSrc]);

  // Initiale src — einmalig per ref bestimmt, damit kein Re-Mount waehrend
  // der Lebensdauer der View die App neu startet.
  const initialSrcRef = useRef<string | null>(null);
  if (initialSrcRef.current === null && app) {
    // FIX 2026-08-11: IMMER den Launch-Flow starten. Direkte Navigation zu
    // basePath (z.B. /fair-kapa/) scheitert wenn der Session-Cookie nach einem
    // Railway-Redeploy ungültig geworden ist. Der Launch-Flow stellt IMMER
    // einen frischen Cookie aus (ein extra Redirect, aber 100% zuverlässig).
    initialSrcRef.current = app.defaultEmbedSrc ?? "about:blank";
  }

  // SSO-Redemption-URLs (`?code=…`) duerfen nicht persistiert werden — der
  // Code ist single-use; ein spaeterer Restore-Versuch wuerde scheitern.
  const isUsableForRestore = (raw: string): boolean => {
    if (!raw || raw === "about:blank") return false;
    if (/[?&]code=/.test(raw)) return false;
    return true;
  };

  // Track mount state for async operations (rAF callbacks).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Persistierung beim Unmount (Wechsel zu anderer App).
  useEffect(() => {
    return () => {
      try {
        const win = iframeRef.current?.contentWindow;
        if (!win) return;
        const path = win.location.pathname + win.location.search;
        if (isUsableForRestore(path)) saveLastView(appId, path);
      } catch {
        /* cross-origin, detached, oder noch nicht geladen — ignorieren */
      }
    };
  }, [appId]);

  // Reload-Event: wenn die aktuell aktive Satellite-App erneut angesteuert
  // wird (Route bleibt gleich, kein Remount), soll die App neu geladen werden.
  useEffect(() => {
    if (!app) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ appId: string }>).detail;
      if (!detail || detail.appId !== appId) return;
      if (!mountedRef.current) return;
      const iframe = iframeRef.current;
      if (!iframe) return;
      const target = app.defaultEmbedSrc ?? "";
      if (!target) return;
      relaunchAttemptedRef.current = false;
      // Use location.replace() to force reload without about:blank intermediate.
      requestAnimationFrame(() => {
        if (!mountedRef.current) return;
        navigateIframe(iframeRef.current, target);
      });
    };
    window.addEventListener("hub-satellite-reload", handler as EventListener);
    return () => window.removeEventListener("hub-satellite-reload", handler as EventListener);
  }, [appId, app]);

  if (!app || app.kind === "hub" || app.kind === "external") {
    return null;
  }

  const handleLoad = () => {
    if (!mountedRef.current) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      const path = win.location.pathname + win.location.search;
      if (isUsableForRestore(path)) saveLastView(appId, path);

      // Auto-Relaunch: Wenn die Satellite-App nach dem Laden keine gueltige
      // Session hat ODER das Iframe auf /login gelandet ist (ungültiger Cookie),
      // einmalig den SSO-Relaunch triggern.
      const cookieName = SESSION_COOKIE_MAP[appId];
      const launchPath = app.defaultEmbedSrc ?? "";
      const isLaunchUrl = path === launchPath || path.startsWith("/launch/");
      const isCodeUrl = /[?&]code=/.test(path);

      // FIX 2026-08-11: Erkennung ob das Iframe auf /login oder / gelandet ist
      // (passiert wenn der Session-Cookie existiert aber ungültig ist — die App
      // gibt 401, der Hub-Proxy redirectet zu /login, und die Hub-SPA rendert
      // die Default-App im Iframe statt der gewünschten Satellite-App).
      const isLoginRedirect = path === "/login" || path === "/" || path.startsWith("/#/");
      const needsRelaunch = !hasCookie(cookieName ?? "") || isLoginRedirect;

      if (
        cookieName &&
        needsRelaunch &&
        !isLaunchUrl &&
        !isCodeUrl &&
        !relaunchAttemptedRef.current &&
        app.defaultEmbedSrc
      ) {
        relaunchAttemptedRef.current = true;
        const targetSrc = app.defaultEmbedSrc;
        // Defer to next frame and use location.replace() — no about:blank.
        requestAnimationFrame(() => {
          if (!mountedRef.current) return;
          navigateIframe(iframeRef.current, targetSrc);
        });
      }
    } catch {
      /* same-origin Pflicht; Satellites laufen unter combsol.app — sollte greifen */
    }
  };

  return (
    <iframe
      ref={iframeRef}
      src={initialSrcRef.current ?? "about:blank"}
      title={app.title}
      onLoad={handleLoad}
      style={{
        width: "100%",
        height: "100%",
        border: "0",
        display: "block",
        background: "var(--bg)",
      }}
    />
  );
}
